import { useCallback, useMemo, useState } from 'react';
import { useSlashCommands } from '../../../../../api/agents-client/agent-client.hooks';
import type { SlashCommand } from '../../../../../api/agents-client/agent-client.type';
import { findCommandTokenStartIndex, groupCommandsByScope, hasCommandToken } from './agent-commands.helpers';
import type { GroupedCommands } from './agent-commands.type';

interface UseSlashCommandInputReturn {
  isOpen: boolean;
  searchQuery: string;
  filteredGroups: GroupedCommands[];
  flatFilteredCommands: SlashCommand[];
  selectedCommand: SlashCommand | null;
  handleInputChange: (value: string, cursorPosition: number) => void;
  handleSelectCommand: (command: SlashCommand) => {
    newValue: string;
    newCursorPosition: number;
  };
  closePopover: () => void;
  clearCommand: () => void;
  formatMessageWithCommand: (text: string, command: SlashCommand | null) => string;
}

/**
 * Custom hook for handling slash command detection, filtering, and selection in the chat input.
 * Mirrors the useMention hook pattern but triggers on `/` instead of `@`.
 * @param {string} currentValue - The current value of the input
 * @returns {UseSlashCommandInputReturn} - The return value of the hook
 */
function useSlashCommandInput(currentValue: string): UseSlashCommandInputReturn {
  const { commands, isLoading } = useSlashCommands();

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [slashStartIndex, setSlashStartIndex] = useState<number | null>(null);
  const [selectedCommand, setSelectedCommand] = useState<SlashCommand | null>(null);

  // Filter commands by search query
  const filteredCommands = useMemo(() => {
    if (isLoading) return [];
    if (!searchQuery) return commands;
    const lowerQuery = searchQuery.toLowerCase();
    return commands.filter((cmd) => cmd.name.toLowerCase().includes(lowerQuery));
  }, [commands, searchQuery, isLoading]);

  // Group filtered results by scope
  const filteredGroups = useMemo(() => groupCommandsByScope(filteredCommands), [filteredCommands]);

  /**
   * Detect `/` trigger in the input and extract search query.
   * Only triggers when `/` is at position 0 or preceded by whitespace/newline
   * to avoid matching URLs like `https://`.
   */
  const handleInputChange = useCallback(
    (value: string, cursorPosition: number) => {
      // If user edits away the selected command's `/name`, clear it
      if (selectedCommand) {
        if (!hasCommandToken(value, selectedCommand.name)) {
          setSelectedCommand(null);
        }
      }

      // Scan backward from cursor for `/`
      let slashIndex = -1;
      for (let i = cursorPosition - 1; i >= 0; i--) {
        if (value[i] === '/') {
          // Valid only at position 0 or preceded by whitespace/newline
          if (i === 0 || value[i - 1] === ' ' || value[i - 1] === '\n') {
            slashIndex = i;
          }
          break;
        }
        // Stop if we hit whitespace (query shouldn't span words)
        if (value[i] === ' ' || value[i] === '\n') {
          break;
        }
      }

      if (slashIndex !== -1) {
        const query = value.substring(slashIndex + 1, cursorPosition);
        // Close if query contains a space (user moved past the command)
        if (!query.includes(' ')) {
          setIsOpen(true);
          setSearchQuery(query);
          setSlashStartIndex(slashIndex);
          return;
        }
      }

      // Close popover if no valid `/` found
      setIsOpen(false);
      setSearchQuery('');
      setSlashStartIndex(null);
    },
    [selectedCommand],
  );

  /**
   * Replace `/query` with `/commandname` in the text.
   * Stores the selected command for later use on submit.
   */
  const handleSelectCommand = useCallback(
    (command: SlashCommand): { newValue: string; newCursorPosition: number } => {
      if (slashStartIndex === null) {
        return { newValue: currentValue, newCursorPosition: currentValue.length };
      }

      const before = currentValue.substring(0, slashStartIndex);
      const after = currentValue.substring(slashStartIndex + searchQuery.length + 1); // +1 for `/`
      const replacement = `/${command.name}`;
      const newValue = before + replacement + ' ' + after.trimStart();
      const newCursorPosition = before.length + replacement.length + 1;

      setSelectedCommand(command);
      setIsOpen(false);
      setSearchQuery('');
      setSlashStartIndex(null);

      return { newValue, newCursorPosition };
    },
    [currentValue, slashStartIndex, searchQuery],
  );

  /** Close the popover without selecting */
  const closePopover = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setSlashStartIndex(null);
  }, []);

  /** Clear the selected command (call after submit) */
  const clearCommand = useCallback(() => {
    setSelectedCommand(null);
  }, []);

  /**
   * Replace `/commandname` in the text with the command's prompt for the backend message.
   * If prompt is null/empty, just removes `/commandname`.
   */
  const formatMessageWithCommand = useCallback((text: string, command: SlashCommand | null): string => {
    if (!command) return text;
    const tag = `/${command.name}`;
    const idx = findCommandTokenStartIndex(text, command.name);
    if (idx === -1) return text;

    const promptText = command.prompt?.trim() || '';
    const replaced = text.substring(0, idx) + promptText + text.substring(idx + tag.length);
    return replaced.trim();
  }, []);

  return {
    isOpen,
    searchQuery,
    filteredGroups,
    flatFilteredCommands: filteredCommands,
    selectedCommand,
    handleInputChange,
    handleSelectCommand,
    closePopover,
    clearCommand,
    formatMessageWithCommand,
  };
}

export { useSlashCommandInput };
